import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
  Button,
  Paper,
  Pagination,
  PaginationItem,
  Snackbar,
  Slide,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import { Refresh, Save } from "@mui/icons-material";
import { FAST_API_BASE_URL } from "constants";
import { extractHubSpotPageContent } from "../utils/hubspot-content-extractor";

const CONTENT_TYPE_MAP = {
  blogs: "hubspot_blog",
  pages: "hubspot_website_page",
  "landing-pages": "hubspot_landing_page",
};

const RESULTS_KEY_MAP = {
  blogs: "blogs",
  pages: "pages",
  "landing-pages": "landing-pages",
};

const DEFAULT_LIMIT = 20;

function normalizeTimestamp(timestamp) {
  if (!timestamp) return null;
  try {
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(timestamp)) return timestamp;
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return null;
    const pad = (n) => String(n).padStart(2, "0");
    return (
      `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
      `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`
    );
  } catch {
    return null;
  }
}

const HubSpotDataDisplay = ({ dataType, domainId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const cursorCache = useRef({ 1: null });
  const prefetchAbortRef = useRef(null);
  const [allCursorsReady, setAllCursorsReady] = useState(false);
  const [pageItems, setPageItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [syncedIds, setSyncedIds] = useState([]);
  const [hubspotLastUpdatedAt, setHubspotLastUpdatedAt] = useState({});
  const contentMapRef = useRef({});
  const [desiredSyncedIds, setDesiredSyncedIds] = useState(new Set());
  const savedSyncedIds = useRef(new Set());

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const webContentType = CONTENT_TYPE_MAP[dataType] || "hubspot_blog";
  const totalPages = Math.ceil(totalItems / limit) || 1;
  const currentPageIds = pageItems.map((i) => i.id);
  const selectedOnPage = currentPageIds.filter((id) => desiredSyncedIds.has(id));
  const allOnPageSelected =
    currentPageIds.length > 0 && selectedOnPage.length === currentPageIds.length;
  const someOnPageSelected =
    selectedOnPage.length > 0 && selectedOnPage.length < currentPageIds.length;

  const hasChanges = () => {
    const saved = savedSyncedIds.current;
    if (saved.size !== desiredSyncedIds.size) return true;
    for (const id of saved) if (!desiredSyncedIds.has(id)) return true;
    return false;
  };

  const fetchOnePage = useCallback(
    async (pageNum, pageLimit, signal) => {
      const after = cursorCache.current[pageNum] ?? null;
      const params = new URLSearchParams({ limit: pageLimit, page: pageNum });
      if (after) params.set("after", after);

      try {
        const response = await fetch(
          `${FAST_API_BASE_URL}/api/hubspot/${dataType}?${params}`,
          { headers: { "DOMAIN-TOKEN": domainId }, signal }
        );
        if (!response.ok) return null;
        const result = await response.json();
        if (!result.success) return null;
        return result;
      } catch (e) {
        if (e.name === "AbortError") return null;
        console.error(`fetchOnePage error (page ${pageNum}):`, e);
        return null;
      }
    },
    [dataType, domainId]
  );

  const prefetchAllCursors = useCallback(
    async (totalCount, pageLimit, signal) => {
      const totalUserPages = Math.ceil(totalCount / pageLimit);
      if (totalUserPages <= 1) { setAllCursorsReady(true); return; }
      for (let p = 2; p <= totalUserPages; p++) {
        if (signal?.aborted) return;
        if (cursorCache.current[p + 1] !== undefined) continue;
        const after = cursorCache.current[p];
        if (after === undefined) break;

        const params = new URLSearchParams({ limit: pageLimit, cursor_only: "true" });
        if (after) params.set("after", after);

        try {
          const res = await fetch(
            `${FAST_API_BASE_URL}/api/hubspot/${dataType}?${params}`,
            { headers: { "DOMAIN-TOKEN": domainId }, signal }
          );
          if (!res.ok) break;
          const result = await res.json();
          if (!result?.success) break;

          const nextAfter = result.next_after || null;
          if (!nextAfter) break;

          cursorCache.current[p + 1] = nextAfter;
        } catch (e) {
          if (e.name !== "AbortError") console.error(`Prefetch error page ${p}:`, e);
          break;
        }
      }

      if (!signal?.aborted) setAllCursorsReady(true);
    },
    [dataType, domainId]
  );


  const fetchPage = useCallback(
    async (pageNum, pageLimit) => {
      setLoading(true);
      setError(null);

      const result = await fetchOnePage(pageNum, pageLimit);

      if (!result) {
        setError(`Failed to fetch ${dataType}. Please try again.`);
        setLoading(false);
        return;
      }

      const items = result[RESULTS_KEY_MAP[dataType]] || [];
      const newSyncedIds = result.synced_ids || [];
      const lastUpdatedMap = result.hubspot_last_updated_at || {};
      const nextAfter = result.next_after || null;
      const total = result.total || items.length;

      // Always cache the next-page cursor when we happen to fetch a page
      if (nextAfter) cursorCache.current[pageNum + 1] = nextAfter;

      setPageItems(items);
      setTotalItems(total);
      setHasMore(result.has_more || false);
      setSyncedIds(newSyncedIds);
      setHubspotLastUpdatedAt(lastUpdatedMap);

      if (pageNum === 1) {
        const initialSet = new Set(newSyncedIds);
        setDesiredSyncedIds(initialSet);
        savedSyncedIds.current = new Set(newSyncedIds);

        // Kick off background cursor prefetch
        const pages = Math.ceil(total / pageLimit) || 1;
        if (pages > 1) {
          if (prefetchAbortRef.current) prefetchAbortRef.current.abort();
          const ctrl = new AbortController();
          prefetchAbortRef.current = ctrl;
          prefetchAllCursors(total, pageLimit, ctrl.signal); // pass total items, not pages
        } else {
          setAllCursorsReady(true);
        }
      }

      // Extract content for items on this page
      items.forEach((item) => {
        if (!item.id || contentMapRef.current[item.id] !== undefined) return;
        if (dataType === "blogs") {
          contentMapRef.current[item.id] = item.postBody || "";
        } else {
          try {
            contentMapRef.current[item.id] = extractHubSpotPageContent(item);
          } catch (err) {
            contentMapRef.current[item.id] = "";
          }
        }
      });

      await autoSyncUpdatedItems(items, newSyncedIds, lastUpdatedMap);
      setLoading(false);
    },
    [dataType, domainId, fetchOnePage, prefetchAllCursors] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Auto-sync stale items ──────────────────────────────────────────────────

  const autoSyncUpdatedItems = async (items, syncedIdList, lastUpdatedMap) => {
    const stale = items.filter((item) => {
      if (!syncedIdList.includes(item.id)) return false;
      const hubspotTs = normalizeTimestamp(item.updated || item.updatedAt || item.createdAt);
      const dbTs = normalizeTimestamp(lastUpdatedMap[item.id]);
      return hubspotTs && dbTs && hubspotTs !== dbTs;
    });
    if (stale.length === 0) return;

    setAutoSyncing(true);
    showSnackbar(`Re-syncing ${stale.length} updated item${stale.length > 1 ? "s" : ""}…`, "info");

    let ok = 0, fail = 0;
    for (const item of stale) {
      try {
        const res = await fetch(`${FAST_API_BASE_URL}/api/web-content`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "DOMAIN-TOKEN": domainId },
          body: JSON.stringify(buildSyncPayload(item)),
        });
        const r = await res.json();
        r.resultType === "Success" ? ok++ : fail++;
      } catch { fail++; }
      await delay(100);
    }

    setAutoSyncing(false);
    if (ok > 0) {
      showSnackbar(
        `Re-synced ${ok} item${ok > 1 ? "s" : ""}` + (fail > 0 ? ` (${fail} failed)` : ""),
        fail > 0 ? "warning" : "success"
      );
      window.dispatchEvent(new CustomEvent("syncStarted"));
    } else if (fail > 0) {
      showSnackbar(`Failed to auto-sync ${fail} item${fail > 1 ? "s" : ""}`, "error");
    }
  };

  // ── Misc helpers ──────────────────────────────────────────────────────────

  const buildSyncPayload = (item) => ({
    web_data: contentMapRef.current[item.id] || "",
    url: item.url || item.publicUrl || "",
    page_title:
      item.name || item.htmlTitle ||
      `Untitled ${dataType === "pages" ? "Page" : dataType === "landing-pages" ? "Landing Page" : "Blog"}`,
    page_id: item.id,
    web_content_type: webContentType,
    hubspot_last_updated_at: item.updated || item.updatedAt || item.createdAt || null,
  });

  const delay = (ms) => new Promise((r) => setTimeout(r, ms));
  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Effects ────────────────────────────────────────────────────────────────

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (dataType && domainId) {
      cursorCache.current = { 1: null };
      setAllCursorsReady(false);
      isFirstRender.current = true;
      setCurrentPage(1);
      fetchPage(1, limit);
    }
    return () => {
      if (prefetchAbortRef.current) prefetchAbortRef.current.abort();
    };
  }, [dataType, domainId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    if (dataType && domainId) fetchPage(currentPage, limit);
  }, [currentPage, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────

  const resetAndFetch = (newLimit) => {
    if (prefetchAbortRef.current) prefetchAbortRef.current.abort();
    cursorCache.current = { 1: null };
    setAllCursorsReady(false);
    isFirstRender.current = true;
    setCurrentPage(1);
    fetchPage(1, newLimit);
  };

  const handleRefresh = () => resetAndFetch(limit);

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setLimit(newLimit);
    resetAndFetch(newLimit);
  };

  // All page buttons are now always clickable once their cursor is cached.
  // The renderItem below will disable buttons that aren't ready yet.
  const handlePageChange = (_, value) => setCurrentPage(value);

  const handleToggle = (id) => {
    setDesiredSyncedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAllOnPage = () => {
    setDesiredSyncedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleResyncAll = async () => {
  const allSyncedIds = [...savedSyncedIds.current];
  if (allSyncedIds.length === 0) {
    showSnackbar("No synced items to resync.", "info");
    return;
  }

  setResyncing(true);
  showSnackbar(`Resyncing selected items..`, "info");

  let ok = 0, fail = 0;
  for (const itemId of allSyncedIds) {
    const item = pageItems.find((i) => i.id === itemId);
    if (!item) { fail++; continue; }
    try {
      const res = await fetch(`${FAST_API_BASE_URL}/api/web-content`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "DOMAIN-TOKEN": domainId },
        body: JSON.stringify(buildSyncPayload(item)),
      });
      const r = await res.json();
      r.resultType === "Success" ? ok++ : fail++;
    } catch { fail++; }
    await delay(100);
  }

  setResyncing(false);
  const parts = [];
  if (ok > 0) parts.push(`${ok} item${ok > 1 ? "s" : ""} resynced`);
  if (fail > 0) parts.push(`${fail} failed`);
  showSnackbar(parts.length ? parts.join(", ") : "Done.", fail > 0 ? "warning" : "success");
  if (ok > 0) window.dispatchEvent(new CustomEvent("syncStarted"));
};

  const handleSave = async () => {
    setSaving(true);
    const saved = savedSyncedIds.current;
    const desired = desiredSyncedIds;
    const toSync = [...desired].filter((id) => !saved.has(id));
    const toUnsync = [...saved].filter((id) => !desired.has(id));

    try {
      if (toUnsync.length > 0) {
        const res = await fetch(`${FAST_API_BASE_URL}/api/unsync/content`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json", "DOMAIN-TOKEN": domainId },
          body: JSON.stringify({
            type: "page",
            item_ids: toUnsync.map((id) => ({ id: `hubspot_${id}` })),
          }),
        });
        const r = await res.json();
        if (r.resultType !== "Success") throw new Error(r.message || "Failed to unsync items");
      }

      let ok = 0, fail = 0;
      for (const itemId of toSync) {
        const item = pageItems.find((i) => i.id === itemId);
        if (!item) { fail++; continue; }
        try {
          const res = await fetch(`${FAST_API_BASE_URL}/api/web-content`, {
            method: "POST",
            headers: { "Content-Type": "application/json", "DOMAIN-TOKEN": domainId },
            body: JSON.stringify(buildSyncPayload(item)),
          });
          const r = await res.json();
          r.resultType === "Success" ? ok++ : fail++;
        } catch { fail++; }
        await delay(100);
      }

      savedSyncedIds.current = new Set(desired);

      const parts = [];
      if (toUnsync.length > 0) parts.push(`${toUnsync.length} items unsynced successfully`);
      if (ok > 0) parts.push(`${ok} sync started and may take few minutes to sync`);
      if (fail > 0) parts.push(`${fail} failed`);
      showSnackbar(parts.length ? parts.join(", ") : "No changes to save.", fail > 0 ? "warning" : "success");

      if (ok > 0 || toUnsync.length > 0) {
        window.dispatchEvent(new CustomEvent("syncStarted"));
        setTimeout(() => fetchPage(currentPage, limit), 2000);
      }
    } catch (err) {
      showSnackbar(err.message || "Failed to save changes.", "error");
    } finally {
      setSaving(false);
    }
  };

  const getTitle = (item) => {
    if (dataType === "contacts") {
      const { firstname, lastname, email } = item.properties || {};
      return `${firstname || ""} ${lastname || ""}`.trim() || email || "No Name";
    }
    if (dataType === "landing-pages") return item.name || item.htmlTitle || "Untitled Landing Page";
    return item.name || item.htmlTitle || `Untitled ${dataType === "pages" ? "Page" : "Blog"}`;
  };

  const getUrl = (item) => item.url || item.publicUrl || item.contentUrl || "";

  const getDisplayLabel = () => {
    const map = {
      contacts: "Contacts", blogs: "Blog Posts", pages: "Pages",
      "landing-pages": "Landing Pages", emails: "Email Campaigns",
    };
    return map[dataType] || dataType.charAt(0).toUpperCase() + dataType.slice(1);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading && pageItems.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#219297" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: "8px" }}
        action={<IconButton size="small" onClick={handleRefresh}><Refresh fontSize="small" /></IconButton>}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="down" />}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ backgroundColor: snackbar.severity === "success" ? "#219297" : undefined, color: "#fff" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={{ borderRadius: "12px", border: "1px solid #e0f0f0", backgroundColor: "#ffffff", p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, pb: 2, borderBottom: "1px solid #e0f0f0" }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#333", fontWeight: 600, mb: 0.5 }}>
              {getDisplayLabel()}
              {(loading || autoSyncing) && <CircularProgress size={16} sx={{ color: "#219297", ml: 2 }} />}
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              {totalItems > 0 ? `${totalItems} total · select items to sync` : "Select items to sync"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={handleRefresh} size="small" disabled={loading || autoSyncing}
                  sx={{ color: "#219297", "&:hover": { backgroundColor: "#f0f9fb" } }}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Box sx={{ minWidth: 130 }}>
              <select value={limit} onChange={handleLimitChange}
                style={{ width: "100%", padding: "6px 10px", borderRadius: "6px", border: "1px solid #e0f0f0", backgroundColor: "#ffffff", fontSize: "14px", color: "#333", outline: "none" }}>
                {[5, 10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{n} per page</option>
                ))}
              </select>
            </Box>
          </Box>
        </Box>

        {!allCursorsReady && totalPages > 1 && (
          <Box sx={{ mb: 2, overflow: "hidden", borderRadius: "4px" }}>
            <LinearProgress sx={{
              height: 3,
              backgroundColor: "#e0f0f0",
              "& .MuiLinearProgress-bar": { backgroundColor: "#219297" },
            }} />
            <Typography variant="caption" sx={{ color: "#aaa", mt: 0.5, display: "block" }}>
              Loading data, please wait...
            </Typography>
          </Box>
        )}

        {pageItems.length === 0 && !loading ? (
          <Box sx={{ textAlign: "center", py: 6, px: 3 }}>
            <Typography variant="body1" color="text.secondary">
              No {getDisplayLabel().toLowerCase()} found in your HubSpot account
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <Checkbox
                checked={allOnPageSelected}
                indeterminate={someOnPageSelected}
                onChange={handleSelectAllOnPage}
                sx={{ color: "#999", "&.Mui-checked": { color: "#219297" }, "&.MuiCheckbox-indeterminate": { color: "#219297" }, p: 0.5 }}
              />
              <Button variant="text" onClick={handleSelectAllOnPage}
                sx={{ textTransform: "none", color: "#219297", fontSize: "0.875rem", fontWeight: 600, p: 0, minWidth: "auto", "&:hover": { backgroundColor: "transparent", textDecoration: "underline" } }}>
                {allOnPageSelected ? "Deselect all" : "Select all"}
              </Button>
              <Typography variant="body2" sx={{ color: "#999", ml: 1 }}>
                ({selectedOnPage.length} of {currentPageIds.length} on this page )
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {pageItems.map((item) => {
                const isSelected = desiredSyncedIds.has(item.id);
                return (
                  <Paper key={item.id} elevation={0}
                    sx={{ p: 2, borderRadius: "8px", border: "1px solid", borderColor: isSelected ? "#219297" : "#e0f0f0", backgroundColor: isSelected ? "#f0f9fb" : "#ffffff", transition: "all 0.2s", "&:hover": { borderColor: "#219297" } }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                      <Checkbox checked={isSelected} onChange={() => handleToggle(item.id)}
                        sx={{ color: "#999", "&.Mui-checked": { color: "#219297" }, mt: -0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: "#333", fontSize: "0.95rem", mb: 0.5 }}>
                          {getTitle(item)}
                        </Typography>
                        {getUrl(item) && (
                          <Typography variant="body2" sx={{ color: "#219297", fontSize: "0.85rem", wordBreak: "break-all" }}>
                            {getUrl(item)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  shape="rounded"
                  renderItem={(item) => {
                    const isPageButton = item.type === "page";
                    const cursorAvailable =
                      !isPageButton ||
                      item.page === 1 ||
                      cursorCache.current[item.page] !== undefined;

                    return (
                      <Tooltip
                        title={isPageButton && !cursorAvailable ? "Loading…" : ""}
                        placement="top"
                      >
                        <span>
                          <PaginationItem
                            {...item}
                            disabled={!cursorAvailable || item.disabled}
                            sx={{ opacity: cursorAvailable ? 1 : 0.35 }}
                          />
                        </span>
                      </Tooltip>
                    );
                  }}
                  sx={{
                    "& .MuiPaginationItem-root": { color: "#666", fontSize: "0.875rem" },
                    "& .MuiPaginationItem-root.Mui-selected": {
                      backgroundColor: "#219297", color: "#fff",
                      "&:hover": { backgroundColor: "#1a767a" },
                    },
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap:2,mt: 3, pt: 2, borderTop: "1px solid #e0f0f0" }}>
                <Tooltip title={resyncing ? "Resync in progress…" : "Re-sync all currently synced items"}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleResyncAll}
                    disabled={resyncing || saving || autoSyncing || savedSyncedIds.current.size === 0}
                    sx={{
                      borderColor: "#219297",
                      color: "#219297",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textTransform: "none",
                      borderRadius: "6px",
                      px: 3,
                      py: 1,
                      "&:hover": { backgroundColor: "#f0f9fb", borderColor: "#1a767a" },
                      "&.Mui-disabled": { borderColor: "#e0f0f0", color: "#999" },
                    }}
                    startIcon={resyncing ? <CircularProgress size={16} sx={{ color: "#219297" }} /> : <Refresh fontSize="small" />}
                  >
                    {resyncing ? "Resyncing…" : "Resync All"}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={!hasChanges() ? "No unsaved changes" : autoSyncing ? "Auto-sync in progress" : ""}>
                <span>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <Save />}
                    onClick={handleSave}
                    disabled={saving || !hasChanges() || autoSyncing}
                    sx={{
                      backgroundColor: hasChanges() ? "#219297" : "#e0f0f0",
                      color: hasChanges() ? "#ffffff" : "#999",
                      fontWeight: 600, fontSize: "0.875rem", textTransform: "none",
                      borderRadius: "6px", px: 3, py: 1,
                      "&:hover": { backgroundColor: hasChanges() ? "#1a767a" : "#e0f0f0" },
                    }}
                  >
                    {saving ? "Saving…" : "Save Changes"}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Paper>
    </>
  );
};

export default HubSpotDataDisplay;